/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { RovoThinkingBarWrapper } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBarWrapper';
import { RovoThinkingReady } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingReady';

import { createDismissInlineEditReadyCommand } from '../../../pm-plugins/decoration/actions';

const messages = defineMessages({
	ready: {
		id: 'fabric.editor.ai.inlineEditReadyBar.ready',
		defaultMessage: 'Your edits are ready',
		description: 'Text shown on the floating bar when an inline edit completes',
	},
});

const styles = cssMap({
	// Floats the ready bar at the bottom-centre of the viewport, matching the
	// other floating thinking/ready bars.
	floatingReadyBar: {
		position: 'fixed',
		bottom: token('space.200'),
		left: '50%',
		transform: 'translateX(-50%)',
		width: 'fit-content',
		// 400px matches the shared Rovo floating ready bar design minimum; no ADS sizing token maps to it.
		minWidth: '400px',
		zIndex: 600,
	},
});

interface InlineEditReadyBarProps {
	editorView: EditorView;
}

/**
 * Floating "ready" bar shown after an inline edit completes. Lives in the
 * always-mounted content component (driven by plugin state) so it survives the
 * prompt/session teardown. View scrolls to the edited node; Close dismisses.
 */
export const InlineEditReadyBar = ({ editorView }: InlineEditReadyBarProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const dismiss = useCallback(() => {
		createDismissInlineEditReadyCommand()(editorView.state, editorView.dispatch);
	}, [editorView]);

	const handleView = useCallback(() => {
		// After an inline edit the selection sits on the edited node, so resolve its
		// DOM element and scroll it into view.
		try {
			const { from } = editorView.state.selection;
			const domAtPos = editorView.domAtPos(from);
			const node =
				domAtPos.node instanceof HTMLElement ? domAtPos.node : domAtPos.node.parentElement;
			node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		} catch {
			// domAtPos can throw if the position is out of range; ignore silently.
		}
		dismiss();
	}, [editorView, dismiss]);

	return (
		<Box xcss={styles.floatingReadyBar}>
			<RovoThinkingBarWrapper>
				<RovoThinkingReady
					onView={handleView}
					onClose={dismiss}
					readyText={formatMessage(messages.ready)}
				/>
			</RovoThinkingBarWrapper>
		</Box>
	);
};
